import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  QrCode,
  Send,
  Bell,
  Settings,
  Home,
  Gift,
  History,
  User,
  Plus,
  Minus,
  ArrowUpRight,
  ArrowDownLeft,
  X,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Wallet
} from "lucide-react";
import { getClientPoints, getPointsHistory, loadClient } from '../Redux/Actions/ClientActions';
import { getFeaturedItems } from '../Redux/Actions/FeaturedItemActions';
import logo from '../image/LogoM.png'; // Added logo import
import './MyPoints.css';
import './ScanPoints.css'; // Shared mobile layout styles

// --- Sub-components ---
const PointsCard = ({ points, walletBalance, memberSince, tier, clientEmail, onQRClick }) => {
  const qrString = `CLIENT_AUTH:${clientEmail}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrString)}&bgcolor=ffffff&color=2A2F6B&margin=10`;

  const getTierColor = (t) => {
    switch (t?.toLowerCase()) {
      case 'bronze': return '#CD7F32'; // Bronze 
      case 'argent': return '#94a3b8'; // Silver (Slate-400)
      case 'or': return '#d97706'; // Gold (Amber-600)
      case 'diamant': return '#0ea5e9'; // Diamond (Sky-500)
      default: return '#CD7F32';
    }
  };

  return (
    <div className="points-card animate-in flex items-center justify-between gap-4">
      <div className="flex flex-col justify-center">
        <div className="mb-2">
          <p className="text-white/80 text-xs font-medium mb-0.5">Solde de Points</p>
          <h3 className="text-2xl font-bold text-white leading-none">
            {points?.toLocaleString() || 0} <span className="text-sm font-normal opacity-80">pts</span>
          </h3>
        </div>
        <div className="mb-3">
          <p className="text-white/80 text-xs font-medium mb-0.5">Solde Portefeuille</p>
          <h3 className="text-2xl font-bold text-white leading-none">
            {walletBalance !== undefined ? parseFloat(walletBalance).toFixed(3) : "0.000"} <span className="text-sm font-normal opacity-80">DT</span>
          </h3>
        </div>

        <div className="flex items-center gap-3">
          <div
            className="px-3 py-1 rounded-full bg-white shadow-sm border border-white/50 text-xs font-extrabold uppercase tracking-wide"
            style={{ color: getTierColor(tier) }}
          >
            {tier}
          </div>
          <p className="text-white/50 text-[10px]">Depuis {memberSince || '2024'}</p>
        </div>
      </div>

      <div className="qr-container-mini shrink-0" onClick={onQRClick} style={{ cursor: 'pointer' }}>
        <div className="qr-wrapper-card" style={{ width: '70px', height: '70px' }}>
          <img
            src={qrUrl}
            alt="Client QR"
            className="qr-code-small"
          />
          <div className="qr-scan-line-mini"></div>
        </div>
        <span className="qr-label-mini" style={{ fontSize: '9px' }}>MON QR</span>
      </div>
    </div>
  );
};

const ActionButton = ({ icon: Icon, label, sublabel, variant, onClick }) => (
  <button
    className={`quick-action-btn ${variant}`}
    onClick={onClick}
  >
    <div className={`p-2 rounded-lg mb-3 ${variant === 'primary' ? 'bg-white/20' : 'bg-primary/10'}`}>
      <Icon className={variant === 'primary' ? 'text-white' : 'text-primary'} size={24} />
    </div>
    <span className="font-bold text-sm">{label}</span>
    <span className="text-[10px] opacity-70">{sublabel}</span>
  </button>
);

const TransactionItem = ({ type, points, merchant, date }) => (
  <div className="compact-activity-item animate-in">
    <div className="activity-left">
      <div className={`activity-mini-icon ${type === 'earn' ? 'tx-earn' : 'tx-redeem'}`}>
        {type === 'earn' ? <Plus size={16} /> : <Minus size={16} />}
      </div>
      <div className="activity-details">
        <p className="activity-name">{merchant}</p>
        <p className="activity-meta">{date}</p>
      </div>
    </div>
    <div className={`activity-pts ${type === 'earn' ? 'text-green-600' : 'text-red-600'}`}>
      {type === 'earn' ? '+' : '-'}{points}
    </div>
  </div>
);

// --- Main Component ---
const MyPoints = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showQRModal, setShowQRModal] = useState(false);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);

  const { client, loading: authLoading, message, error } = useSelector(state => state.clientReducer);
  const {
    points,
    history,
    loading: pointsLoading
  } = useSelector(state => state.pointsReducer);
  const { items: featuredItems, loading: featuredLoading } = useSelector(state => state.featuredItemReducer);

  // Charger le client au montage pour rafraîchir le solde et les infos
  useEffect(() => {
    dispatch(loadClient());
    dispatch(getFeaturedItems());
  }, [dispatch]);

  // Charger les points et l'historique quand l'ID client est disponible
  useEffect(() => {
    if (client?._id) {
      dispatch(getClientPoints(client._id));
      dispatch(getPointsHistory(client._id, 10));
    }
  }, [dispatch, client?._id]);

  useEffect(() => {
    if (!client && !authLoading) {
      navigate('/login-client');
    }
  }, [client, authLoading, navigate]);

  const getLevel = (pts) => {
    if (pts >= 5000) return 'Diamant';
    if (pts >= 2500) return 'Or';
    if (pts >= 1500) return 'Argent';
    return 'Bronze';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short'
    });
  };

  // --- Horizontal Draggable Scroll Logic ---
  const useDraggableScroll = (ref) => {
    const isDown = useRef(false);
    const startX = useRef(0);
    const scrollLeft = useRef(0);

    const onMouseDown = (e) => {
      isDown.current = true;
      ref.current.classList.add('active');
      startX.current = e.pageX - ref.current.offsetLeft;
      scrollLeft.current = ref.current.scrollLeft;
    };

    const onMouseLeave = () => {
      isDown.current = false;
      ref.current?.classList.remove('active');
    };

    const onMouseUp = () => {
      isDown.current = false;
      ref.current?.classList.remove('active');
    };

    const onMouseMove = (e) => {
      if (!isDown.current) return;
      e.preventDefault();
      const x = e.pageX - ref.current.offsetLeft;
      const walk = (x - startX.current) * 2; // scroll-fast scale
      ref.current.scrollLeft = scrollLeft.current - walk;
    };

    return { onMouseDown, onMouseLeave, onMouseUp, onMouseMove };
  };

  const bestSellersRef = useRef(null);
  const partnersRef = useRef(null);

  const bestSellersEvents = useDraggableScroll(bestSellersRef);
  const partnersEvents = useDraggableScroll(partnersRef);

  const handleItemClick = (link) => {
    if (link) {
      if (link.startsWith('http')) {
        window.location.href = link;
      } else {
        navigate(link);
      }
    }
  };

  const handlePartnerClick = (item) => {
    setSelectedPartner(item);
    setShowPartnerModal(true);
  };

  // Filter Data
  const bestSellers = featuredItems ? featuredItems.filter(item => item.type === 'best_seller') : [];
  const partnerOffers = featuredItems ? featuredItems.filter(item => item.type === 'partner_offer') : [];

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('data:') || url.startsWith('http')) {
      return url;
    }
    return null;
  };

  if (authLoading || pointsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!client) return null;

  return (
    <div className="mobile-app-container">
      {/* Header */}
      <header className="mobile-native-header">
        <div className="header-main-content">
          <div className="user-info">
            <p className="text-xs text-muted-foreground">Bonjour,</p>
            <h1 className="header-title-premium text-2xl">{client.name}</h1>
          </div>

          {/* Logo Centered */}
          <div className="flex-1 flex justify-center">
            <img src={logo} alt="Logo" style={{ height: '100px', objectFit: 'contain' }} />
          </div>

          <div className="header-actions flex gap-2">
            <button className="p-2 hover:bg-secondary rounded-full">
              <Bell size={20} />
            </button>
            <button className="p-2 hover:bg-secondary rounded-full" onClick={() => navigate('/profile')}>
              <Settings size={20} />
            </button>
          </div>
        </div>
      </header>

      <main className="mobile-main-feed">
        <div className="space-y-6">
          <PointsCard
            points={points}
            walletBalance={client.walletBalance}
            memberSince={client.createdAt ? new Date(client.createdAt).getFullYear() : '2024'}
            tier={getLevel(points)}
            clientEmail={client.email}
            onQRClick={() => setShowQRModal(true)}
          />

          <div className="action-buttons-grid">
            <ActionButton
              icon={QrCode}
              label="Scanner"
              sublabel="Gagner des points"
              variant="primary"
              onClick={() => navigate('/scan-points')}
            />
            <ActionButton
              icon={Wallet}
              label="Mon Wallet"
              sublabel="Gérer mon argent"
              variant="secondary"
              onClick={() => navigate('/wallet')}
            />
            <ActionButton
              icon={Send}
              label="Récompenses"
              sublabel="Échanger des points"
              variant="primary"
              onClick={() => navigate('/rewards')}
            />
          </div>

          {/*       {/* --- New Slider 1: Best Sellers --- */}
          {/* <section className="mb-2">
            <h3 className="section-title">Produits les plus vendus</h3>
            <div className="categories-wrapper">
              <div
                className="mobile-category-bar"
                ref={bestSellersRef}
                {...bestSellersEvents}
              >
                {bestSellers.map(item => (
                  <button
                    key={item._id}
                    className="category-sq-btn"
                    style={{ backgroundImage: `url(${getImageUrl(item.imageUrl)})` }}
                    onClick={() => handleItemClick(item.link)}
                  >
                    <span className="cat-sq-label">{item.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </section> */}

          {/* --- New Slider 2: Partner Offers --- */}
          <section className="mb-4">
            <h3 className="section-title">Offres partenaires</h3>
            <div className="categories-wrapper">
              <div
                className="mobile-category-bar"
                ref={partnersRef}
                {...partnersEvents}
              >
                {partnerOffers.map(item => (
                  <button
                    key={item._id}
                    className="category-sq-btn"
                    style={{ backgroundImage: `url(${getImageUrl(item.imageUrl)})` }}
                    onClick={() => handlePartnerClick(item)}
                  >
                    <span className="cat-sq-label">{item.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="section-title">Activité récente</h2>
              <button
                onClick={() => navigate('/history')}
                className="view-all-btn"
                style={{ fontSize: '12px', padding: '2px 10px' }}
              >
                Voir tout
              </button>
            </div>

            <div className="compact-activity-list">
              {history && history.length > 0 ? (
                history.slice(0, 4).map((tx, i) => (
                  <TransactionItem
                    key={i}
                    type={tx.points > 0 ? 'earn' : 'redeem'}
                    points={Math.abs(tx.points)}
                    merchant={tx.description || "Achat Boutique"}
                    date={formatDate(tx.date || tx.createdAt)}
                  />
                ))
              ) : (
                <p className="text-center py-4 text-muted-foreground text-sm w-full">Aucune activité récente</p>
              )}
            </div>
          </section>
        </div>
      </main>

      {/* Partner Offer Modal */}
      {showPartnerModal && selectedPartner && (
        <div className="partner-modal-overlay" onClick={() => setShowPartnerModal(false)}>
          <div className="partner-modal" onClick={e => e.stopPropagation()}>
            <div className="sheet-header justify-end mb-2">
              <button className="sheet-close-btn bg-slate-100 hover:bg-slate-200" onClick={() => setShowPartnerModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="sheet-body flex flex-col items-center text-center">

              <div className="w-24 h-24 rounded-2xl overflow-hidden shadow-lg mb-6 border border-slate-100">
                <img
                  src={getImageUrl(selectedPartner.imageUrl)}
                  alt={selectedPartner.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <h3 className="text-2xl font-bold text-slate-800 mb-2">{selectedPartner.title}</h3>

              {selectedPartner.subtitle && (
                <p className="text-slate-500 mb-6 px-4 leading-relaxed">
                  {selectedPartner.subtitle}
                </p>
              )}

              {selectedPartner.link && (
                <button
                  className="sheet-primary-btn flex items-center justify-center gap-2"
                  onClick={() => {
                    handleItemClick(selectedPartner.link);
                    setShowPartnerModal(false);
                  }}
                >
                  <span>Voir l'offre</span>
                  <ExternalLink size={18} />
                </button>
              )}

              <button className="mt-4 text-slate-400 text-sm font-medium p-2" onClick={() => setShowPartnerModal(false)}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal Overlay */}
      {showQRModal && (
        <div className="mobile-bottom-sheet-overlay" onClick={() => setShowQRModal(false)}>
          <div className="mobile-bottom-sheet" onClick={e => e.stopPropagation()}>
            <div className="sheet-grabber"></div>

            <div className="sheet-header">
              <h2 className="sheet-title">Ma Carte Fidélité</h2>
              <button className="sheet-close-btn" onClick={() => setShowQRModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="sheet-body">
              <div className="sheet-qr-display">
                <p className="text-slate-500 text-sm mb-6 leading-relaxed">
                  Présentez ce code à l'administrateur <br />
                  <span className="font-bold text-primary">pour ajouter vos points</span>
                </p>

                <div className="sheet-qr-container larger">
                  <div className="qr-scan-line"></div>
                  <img
                    className="qr-code-image-large"
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(`CLIENT_AUTH:${client.email}`)}&bgcolor=ffffff&color=2A2F6B&margin=15`}
                    alt="Mon QR Code"
                  />
                </div>

                <div className="mt-8 flex items-center gap-2 justify-center text-primary font-bold bg-primary/5 py-4 rounded-2xl border border-primary/10">
                  <CheckCircle2 size={20} className="text-primary" />
                  <span className="text-sm">QR Code Client Actif</span>
                </div>
              </div>

              <button className="sheet-primary-btn mt-6" onClick={() => setShowQRModal(false)}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shared Floating Bottom Nav */}
      <nav className="mobile-floating-nav">
        <button className="nav-btn is-active">
          <div className="nav-active-dot"></div>
          <Home size={22} />
          <span>Accueil</span>
        </button>
        <button className="nav-btn" onClick={() => navigate('/rewards')}>
          <Gift size={22} />
          <span>Cadeaux</span>
        </button>
        <button className="nav-btn" onClick={() => navigate('/scan-points')}>
          <div className="nav-center-scan">
            <QrCode size={24} color="white" />
          </div>
        </button>
        <button className="nav-btn" onClick={() => navigate('/wallet')}>
          <Wallet size={22} />
          <span>Wallet</span>
        </button>
        <button className="nav-btn" onClick={() => navigate('/profile')}>
          <User size={22} />
          <span>Profil</span>
        </button>
      </nav>
    </div>
  );
};

export default MyPoints;